import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion, useScroll, useSpring } from 'framer-motion';
import MonitorCheck from 'lucide-react/dist/esm/icons/monitor-check.js';
import Shirt from 'lucide-react/dist/esm/icons/shirt.js';
import Truck from 'lucide-react/dist/esm/icons/truck.js';
import UploadCloud from 'lucide-react/dist/esm/icons/upload-cloud.js';
import './HomeProcessJourney.css';

const steps = [
  { icon: Shirt, number: '01', title: 'בוחרים מוצר', text: 'מתחילים מסוג הבגד, הצבע והכמות שמתאימים לכם.', to: '/catalog' },
  { icon: UploadCloud, number: '02', title: 'מעלים עיצוב', text: 'קובץ, לוגו או רעיון — מצרפים אותו ישירות להזמנה.' },
  { icon: MonitorCheck, number: '03', title: 'מאשרים מוקאפ', text: 'רואים את המיקום והגודל לפני שהייצור מתחיל.' },
  { icon: Truck, number: '04', title: 'מקבלים משלוח', text: 'אנחנו מייצרים, אורזים ושולחים עד אליכם.' },
];

function useMediaQuery(query) {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, [query]);

  return matches;
}

const HomeProcessJourney = () => {
  const reduceMotion = useReducedMotion();
  const isMobile = useMediaQuery('(max-width: 720px)');
  const processLineRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: processLineRef,
    offset: isMobile ? ['start 90%', 'end 18%'] : ['start 92%', 'start 18%'],
  });
  const lineProgress = useSpring(scrollYProgress, { stiffness: 95, damping: 24, mass: 0.45 });

  return (
    <section className="home-process" id="how" aria-labelledby="home-process-title">
      <motion.div
        className="home-process__heading"
        initial={reduceMotion ? false : { opacity: 0, y: 22 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.55 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      >
        <span>ארבעה שלבים, בלי ניחושים</span>
        <h2 id="home-process-title">יודעים איפה ההזמנה עומדת</h2>
        <p>בכל שלב תדעו מה קורה — מבחירת הבגד ועד שההזמנה מגיעה אליכם.</p>
      </motion.div>

      <div className="home-process__line" role="list" ref={processLineRef}>
        {!reduceMotion && (
          <motion.div
            className="home-process__progress"
            aria-hidden="true"
            style={isMobile ? { scaleY: lineProgress } : { scaleX: lineProgress }}
          />
        )}

        {steps.map(({ icon: Icon, number, title, text, to }, index) => (
          <motion.article
            className="home-process__step"
            role="listitem"
            key={number}
            initial={reduceMotion ? false : { opacity: 0, y: 34, scale: 0.97 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={{ duration: 0.55, delay: index * 0.11, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="home-process__marker" aria-hidden="true"><span /></div>
            <div className="home-process__step-top">
              <span>{number}</span>
              <motion.div
                className="home-process__icon"
                initial={reduceMotion ? false : { scale: 0.65, rotate: -16 }}
                whileInView={{ scale: 1, rotate: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ type: 'spring', stiffness: 240, damping: 16, delay: 0.16 + index * 0.11 }}
              >
                <Icon size={26} strokeWidth={1.7} />
              </motion.div>
            </div>
            <h3>
              {to ? (
                <Link to={to} className="home-process__start-link">
                  {title} <span aria-hidden="true">←</span>
                </Link>
              ) : title}
            </h3>
            <p>{text}</p>
          </motion.article>
        ))}
      </div>
    </section>
  );
};

export default HomeProcessJourney;
